import { saleApi } from '@/app/api/saleApi';
import { idempotencyKey, clearIdempotencyKey } from '@/features/checkout/ordersApi';
import { markWaiterSendPending } from './waiterCartSlice';

/** Upper bound on a round POST — see `postRound`. */
export const POST_ROUND_TIMEOUT_MS = 20000;

// ---------------------------------------------------------------------------
// Wire shapes (spec §2.3) — snake_case as returned by sale_api's
// order/waiterMode/{views,serializers,services}.py.
// ---------------------------------------------------------------------------

export interface WaiterRestaurantSummary {
  uuid: string;
  name: string;
}

interface RawUnlockResponse {
  token: string;
  device_uuid: string;
  waiter_names: string[];
  idle_lock_seconds: number;
  restaurant: WaiterRestaurantSummary;
  expires_at: string;
}

export interface UnlockResult {
  token: string;
  deviceUuid: string;
  waiterNames: string[];
  idleLockSeconds: number;
  restaurant: WaiterRestaurantSummary;
  expiresAt: string;
}

/** SC-776 — the PIN *shape* the lock screen needs before the waiter types:
 *  how many dots to draw, and how many digits to wait for before verifying.
 *  `pinLength` is null when the backend has no 4/6-digit PIN to describe (or
 *  the call failed); PinPad then falls back to its legacy 4-then-6 probe. */
export interface WaiterPinPolicy {
  pinLength: number | null;
}

export interface UnlockArgs {
  restaurantUuid: string;
  pin: string;
  deviceId: string;
  deviceLabel?: string;
  waiterName?: string;
  takeoverDeviceUuid?: string;
  /** SC-576 — the waiter confirmed signing the other device(s) holding
   * `waiterName` out. Without it the server answers 409 WAITER_NAME_IN_USE. */
  forceNameTakeover?: boolean;
}

function transformUnlock(raw: RawUnlockResponse): UnlockResult {
  return {
    token: raw.token,
    deviceUuid: raw.device_uuid,
    waiterNames: raw.waiter_names,
    idleLockSeconds: raw.idle_lock_seconds,
    restaurant: raw.restaurant,
    expiresAt: raw.expires_at,
  };
}

/** SC-799 — `POST renew/` : le meme appareil re-signe son propre jeton. Seuls
 *  le jeton et son echeance changent (le nom, l'appareil et le restaurant sont
 *  ceux du jeton presente). */
export interface RenewResult {
  token: string;
  expiresAt: string;
}

/** Un plat de la ronde en attente, tel que l'écran de validation le lit —
 *  sans prix : le total de la ronde est déjà sur la ligne, et le serveur
 *  accepte ou refuse la ronde entière, jamais une ligne. */
export interface PendingRoundItem {
  uuid: string;
  name: string;
  qte: number;
  options: WaiterOrderItemOption[];
  comment: string;
}

export interface PendingValidationRound {
  orderUuid: string;
  roundNumber: number;
  itemsCount: number;
  total: string;
  createdAt: string;
  /** Les plats que `itemsCount` compte. Vide sur un serveur antérieur, qui
   *  n'envoyait que le compte — l'écran retombe alors sur le compte seul. */
  items: PendingRoundItem[];
}

/** SC-953 — an option of a taken-over dish, as sale_api stored it from the
 *  quote: the value uuid is what the cart line needs to re-send it. */
export interface TakenOverOption {
  option_value_uuid?: string;
  name?: string;
  qty?: number;
}

/** SC-953 — a dish handed back by `take-over/`, enough to rebuild a waiter
 *  cart line even when the menu no longer carries the product. */
export interface TakenOverItem {
  uuid: string;
  productUuid: string;
  name: string;
  code: string;
  qte: number;
  /** Euros, as a decimal string — the price the customer was quoted. */
  unitTtc: string;
  options: TakenOverOption[];
  comment: string;
}

export interface TakenOverRound {
  orderUuid: string;
  sessionUuid: string | null;
  roundNumber: number;
  items: TakenOverItem[];
}

interface RawTakenOverItem {
  uuid: string;
  product_uuid: string;
  name: string;
  code?: string;
  qte: number;
  unit_ttc: string;
  options?: TakenOverOption[];
  comment?: string;
}

interface RawTakenOverRound {
  order_uuid: string;
  session_uuid: string | null;
  round_number: number;
  items: RawTakenOverItem[];
}

function transformTakenOverRound(raw: RawTakenOverRound): TakenOverRound {
  return {
    orderUuid: raw.order_uuid,
    sessionUuid: raw.session_uuid,
    roundNumber: raw.round_number,
    items: (raw.items ?? []).map((item) => ({
      uuid: item.uuid,
      productUuid: item.product_uuid,
      name: item.name,
      code: item.code ?? '',
      qte: item.qte,
      unitTtc: item.unit_ttc,
      options: item.options ?? [],
      comment: item.comment ?? '',
    })),
  };
}

// Design §2.2/§3 (B1/B2 backend slices) — the salle (Area) a table belongs
// to. `null` means unassigned. This is the SLIM shape the `tables/` row
// embeds; `WaiterArea` below (the `areas/` list/create endpoints) extends it
// with the fields only the salle-management screens need.
export interface WaiterTableAreaRef {
  uuid: string;
  name: string;
  color: string | null;
}

interface RawWaiterTableAreaRef {
  uuid: string;
  name: string;
  color: string | null;
}

function transformAreaRef(raw: RawWaiterTableAreaRef | null | undefined): WaiterTableAreaRef | null {
  if (!raw) return null;
  return { uuid: raw.uuid, name: raw.name, color: raw.color ?? null };
}

export interface WaiterArea extends WaiterTableAreaRef {
  displayOrder: number;
  tableCount: number;
}

interface RawWaiterArea extends RawWaiterTableAreaRef {
  display_order: number;
  table_count: number;
}

function transformArea(raw: RawWaiterArea): WaiterArea {
  return {
    ...(transformAreaRef(raw) as WaiterTableAreaRef),
    displayOrder: raw.display_order,
    tableCount: raw.table_count,
  };
}

interface RawPendingRoundItem {
  uuid: string;
  name: string;
  qte: number;
  options?: WaiterOrderItemOption[];
  comment?: string;
}

interface RawPendingValidationRound {
  order_uuid: string;
  round_number: number;
  items_count: number;
  total: string;
  created_at: string;
  items?: RawPendingRoundItem[];
}

function transformPendingValidation(raw: RawPendingValidationRound[] | undefined): PendingValidationRound[] {
  return (raw ?? []).map((r) => ({
    orderUuid: r.order_uuid,
    roundNumber: r.round_number,
    itemsCount: r.items_count,
    total: r.total,
    createdAt: r.created_at,
    items: (r.items ?? []).map((item) => ({
      uuid: item.uuid,
      name: item.name,
      qte: item.qte,
      options: item.options ?? [],
      comment: item.comment ?? '',
    })),
  }));
}

// Design §1.6/§2 — `dine_in_table` (existing table sessions) vs `takeaway`
// (Emporter notes, B1/B2 backend slices, `TableSession.table` nullable).
export type WaiterSessionKind = 'dine_in_table' | 'takeaway';

export interface WaiterTableSession {
  uuid: string;
  // SC-377: service mode of the live session (buffet/ordering). The waiter front
  // drives buffet pricing (Inclus / 0-base) off this, mirroring the scanned-QR flow.
  mode: 'ordering' | 'buffet';
  covers: number;
  openedAt: string;
  expiresAt: string;
  roundCount: number;
  openedBy: string;
  openedByWaiterName: string | null;
  runningTotal: string;
  pendingValidation: PendingValidationRound[];
  lastRoundAt: string | null;
  // Design §1.6/§2 — added by the B1 backend slice to the session serializer
  // shared by `tables/` and `sessions/<uuid>/`. Optional: an older server (or
  // a test fixture predating the takeaway feature) omits them.
  kind?: WaiterSessionKind;
  label?: string | null;
  // SC-1466 — a takeaway note opened with « Plus tard » still carries its
  // « Client N » placeholder. Optional: an older server omits it.
  labelProvisional?: boolean;
  // F2/TicketPlats — buffet round-budget gating (04-ux-mobile-spec.md §2.1,
  // mirrors `useBuffetRoundGate`'s guest-facing fields). Not yet in the §2
  // contract table; optional so a server that doesn't emit it yet degrades to
  // "no budget gate" instead of breaking the session shape.
  buffetRoundItemBudget?: number | null;
  // F2 follow-up (team-lead backend confirmation, 2026-08-27) — `GET
  // sessions/<uuid>/` returns 200 for a CLOSED session too (`closed_at` set);
  // 404 is only unknown/cross-tenant. `TicketPage`'s re-join reads this
  // BEFORE ever calling `tables/<t>/session/` (open-or-join — calling it on a
  // closed table's uuid would open a brand-new session, a real Salle state
  // change) or `takeaway/<uuid>/session/`. Absent on `tables/`/`takeaway/`
  // list entries (those only ever list OPEN sessions) — optional so those
  // callers are unaffected.
  status?: 'open' | 'closed' | string;
  // Design §2.2/§3 (B1 backend slice) — derived server-side so the front
  // never re-implements the payment/lock predicates (`tableState.ts`'s
  // `deriveTableState`/`isLocked` read these, not raw orders). Optional for
  // the same reason as `kind`/`label`/`status` above: an older server, or a
  // fixture predating the salle-ux feature, omits them — the front then
  // falls back to "not paid, not locked" rather than crashing.
  dueTotal?: string;
  isPaid?: boolean;
  // Spec 2026-08-28 paid-table-ordering-lock §5 — ce qui est DEJA encaisse, et
  // si le convive s'est verrouille en reglant. Sans `paidTotal`, une table qui
  // a paye 100 EUR puis rappele 20 EUR est indiscernable d'une table qui doit
  // 120 EUR sans avoir jamais rien paye : `isPaid` vaut faux dans les deux cas.
  // Optionnels comme leurs voisins — un serveur anterieur ne les envoie pas.
  paidTotal?: string;
  orderingLocked?: boolean;
  // spec 2026-08-28 §6 — un convive est en train de payer sa table depuis son
  // telephone. CONSULTATIF : le comptoir n'est jamais bloque, mais le serveur
  // doit voir qu'un paiement est en vol avant de reclamer la meme somme.
  // Optionnel comme ses voisins : un serveur anterieur ne l'envoie pas.
  guestPaymentPending?: boolean;
  caisseHandoffStatus?: 'requested' | 'imported' | 'failed' | null;
  // SC-976 — spec §3.4/D2/D3: a table session can now span several physical
  // tables (« tables réunies »). `tableLabel` is the human string that
  // REPLACES a bare number everywhere a person reads it (`5+6`, `+` no
  // space) — never recomposed front-end, always the server's string.
  // `tableUuids`/`tableNumbers` are the structured lists behind it, anchor
  // first (D3). Optional/defaulting like every field above: a server
  // predating this feature omits them, and a session with no rattachement
  // degrades to `isMerged: false` — byte-identical to today (E-42).
  tableLabel?: string;
  tableUuids?: string[];
  tableNumbers?: (string | number)[];
  isMerged?: boolean;
}

interface RawWaiterTableSession {
  uuid: string;
  mode: 'ordering' | 'buffet';
  covers: number;
  opened_at: string;
  expires_at: string;
  round_count: number;
  opened_by: string;
  opened_by_waiter_name: string | null;
  running_total: string;
  pending_validation: RawPendingValidationRound[];
  last_round_at: string | null;
  kind?: WaiterSessionKind;
  label?: string | null;
  label_provisional?: boolean;
  buffet_round_item_budget?: number | null;
  status?: string;
  due_total?: string;
  is_paid?: boolean;
  paid_total?: string;
  ordering_locked?: boolean;
  guest_payment_pending?: boolean;
  caisse_handoff_status?: 'requested' | 'imported' | 'failed' | null;
  // SC-976 — design §3.4 table: the existing `table_number` field (carried
  // on this same session block by the full backend serializer, even though
  // nothing here read it before) now RECEIVES the tablée label (D2); the
  // structured lists are new. `table_label` wins when both are present.
  table_label?: string | null;
  table_number?: string | number;
  table_uuids?: string[];
  table_numbers?: (string | number)[];
  is_merged?: boolean;
}

export interface WaiterTable {
  uuid: string;
  number: string | number;
  status: string;
  // SC-380: table capacity (defaults the covers prompt) and the covers ceiling
  // (capacity + tolerance) the stepper clamps to.
  capacity: number;
  maxCovers: number;
  session: WaiterTableSession | null;
  // Design §2.2/§3 (B1 backend slice) — the salle this table belongs to,
  // `null` when unassigned. Optional: an older server (or a fixture
  // predating the salle feature) omits the field entirely; `tableState.ts`'s
  // `filterTablesBySalle` treats missing and `null` identically ("sans
  // salle").
  area?: WaiterTableAreaRef | null;
  // SC-976 — design §3.4: which role THIS physical table plays in its
  // session, `null` on a lone table (or an older server). A member row
  // carries the SAME `session` object (same uuid) as its anchor row — the
  // grid renders one tile per table, this is what tells them apart.
  role?: 'anchor' | 'member' | null;
  // DF-0349 — la table est partie en caisse il y a moins de 30 min : quand, et
  // à la demande de qui. Seulement sur une table libre (`null` dès qu'elle est
  // rouverte) ; absent d'un serveur antérieur.
  lastCaisseHandoff?: WaiterCaisseHandoffTrace | null;
}

export interface WaiterCaisseHandoffTrace {
  at: string;
  by: string;
}

interface RawWaiterTable {
  uuid: string;
  number: string | number;
  status: string;
  capacity: number;
  max_covers: number;
  session: RawWaiterTableSession | null;
  area?: RawWaiterTableAreaRef | null;
  role?: 'anchor' | 'member' | null;
  last_caisse_handoff?: { at: string; by?: string } | null;
}

// SC-569 — actions de table que la sortie ticket du restaurant permet.
export interface PrintCapabilities {
  canPrintTotal: boolean;
  canSendToCaisse: boolean;
  // SC-1038 — réimprimer le bon d'UN poste de cuisine. Distinct de
  // `canPrintTotal` : l'addition et le bon de cuisine ne sortent pas du même
  // appareil, un restaurant peut très bien avoir une sortie ticket sans avoir
  // la moindre imprimante de cuisine à viser.
  canReprintKitchen: boolean;
  // SC-1457 — `<pont>/s/<clé>/` : le lien du QR des tickets, sans sa
  // référence. Vide pour tout restaurant sans pont vers l'ancienne caisse.
  caisseBridgeLinkBase: string;
}

interface RawPrintCapabilities {
  can_print_total: boolean;
  can_send_to_caisse: boolean;
  can_reprint_kitchen: boolean;
  caisse_bridge_link_base?: string;
}

export const NO_PRINT_CAPABILITIES: PrintCapabilities = {
  canPrintTotal: false,
  canSendToCaisse: false,
  canReprintKitchen: false,
  caisseBridgeLinkBase: '',
};

/**
 * SC-1457 — le lien que le ✔ ouvre pour mettre une addition dans l'ancienne
 * caisse : celui du QR imprimé sur le ticket, suivi de `#auto` pour que le pont
 * envoie sans second appui. `ref` suit la règle du ticket — l'uuid de la
 * session d'une table ou d'une note, l'uuid de la commande pour une commande
 * en ligne — sans quoi le pont ne reconnaîtrait pas un envoi déjà fait.
 *
 * `null` dès qu'une caisse liée prend déjà « Envoyer en caisse », ou qu'aucun
 * pont n'est réglé : le ✔ garde alors exactement son comportement d'avant.
 */
export function caisseBridgeLink(capabilities: PrintCapabilities | undefined, ref: string): string | null {
  if (!capabilities || capabilities.canSendToCaisse || !capabilities.caisseBridgeLinkBase || !ref) return null;
  return `${capabilities.caisseBridgeLinkBase}${encodeURIComponent(ref)}#auto`;
}

// SC-1038 — un poste de cuisine tel que le serveur le nomme. Le mobile n'a
// besoin de rien d'autre : le routage des plats vers ce poste est fait côté
// sale_api, le téléphone ne transmet qu'un uuid.
export interface KitchenPrinter {
  uuid: string;
  name: string;
}

// SC-577 — où part l'addition. Aucune des trois sorties n'imprime dans la
// réponse : le serveur confie l'addition au navigateur du comptoir, à la caisse
// ou à l'imprimante cloud, et il faut aller lire ce que la demande est devenue.
// La destination sert à nommer la bonne sortie dans le message — « le comptoir »
// ne veut rien dire d'une imprimante cloud.
export type PrintTotalDestination = 'cloud' | 'clodop' | 'caisse' | '';

export interface PrintTotalQueued {
  destination: PrintTotalDestination;
  dispatchUuid: string;
}

// `waiting` = personne ne l'a prise · `printing` = un écran s'en occupe ·
// `printed` = papier confirmé · `failed` = un écran a essayé sans y arriver.
export type PrintTotalState = 'waiting' | 'printing' | 'printed' | 'failed';

export interface PrintTotalStatus {
  state: PrintTotalState;
  detail: string;
  // Relue à chaque interrogation plutôt que mémorisée depuis la demande : c'est
  // la ligne d'attente qui fait foi, la même source que celle qui a routé
  // l'impression.
  destination: PrintTotalDestination;
}

function transformSession(raw: RawWaiterTableSession): WaiterTableSession {
  return {
    uuid: raw.uuid,
    mode: raw.mode,
    covers: raw.covers,
    openedAt: raw.opened_at,
    expiresAt: raw.expires_at,
    roundCount: raw.round_count,
    openedBy: raw.opened_by,
    openedByWaiterName: raw.opened_by_waiter_name,
    runningTotal: raw.running_total,
    pendingValidation: transformPendingValidation(raw.pending_validation),
    lastRoundAt: raw.last_round_at,
    kind: raw.kind,
    label: raw.label,
    labelProvisional: raw.label_provisional === true,
    buffetRoundItemBudget: raw.buffet_round_item_budget ?? null,
    status: raw.status,
    dueTotal: raw.due_total,
    isPaid: raw.is_paid,
    paidTotal: raw.paid_total,
    orderingLocked: raw.ordering_locked ?? false,
    guestPaymentPending: raw.guest_payment_pending ?? false,
    caisseHandoffStatus: raw.caisse_handoff_status ?? null,
    tableLabel: raw.table_label ?? (raw.table_number !== undefined ? String(raw.table_number) : undefined),
    tableUuids: raw.table_uuids ?? [],
    tableNumbers: raw.table_numbers ?? [],
    isMerged: raw.is_merged === true,
  };
}

function transformTable(raw: RawWaiterTable): WaiterTable {
  return {
    uuid: raw.uuid,
    number: raw.number,
    status: raw.status,
    capacity: raw.capacity,
    maxCovers: raw.max_covers,
    session: raw.session ? transformSession(raw.session) : null,
    area: transformAreaRef(raw.area),
    role: raw.role ?? null,
    lastCaisseHandoff: raw.last_caisse_handoff
      ? { at: raw.last_caisse_handoff.at, by: raw.last_caisse_handoff.by ?? '' }
      : null,
  };
}

// Design §3 `DELETE tables/<uuid>/` — hard delete when the table has no
// history, else archive (`is_active=False`, same as the staff `destroy`).
export interface ArchiveTableResult {
  detail: string;
  archived: boolean;
}

interface RawArchiveTableResult {
  detail?: string;
  archived?: boolean;
}

export interface OpenJoinTableResult {
  guestToken: string;
  session: WaiterTableSession;
}

interface RawOpenJoinTableResponse {
  guest_token: string;
  session: RawWaiterTableSession;
}

// ---------------------------------------------------------------------------
// Emporter notes (design §1.6, contract §2 `takeaway/`) — a note is a
// `TableSession` without a table (`table` nullable), so its wire shape is the
// table session's minus the table-only fields (`mode`, `expires_at`).
// ---------------------------------------------------------------------------

export interface WaiterTakeawayNote {
  uuid: string;
  kind: 'takeaway';
  label: string;
  /** SC-1466 — still the « Client N » placeholder of a « Plus tard ».
   *  Optional: an older server (or a fixture) omits it. */
  labelProvisional?: boolean;
  covers: number;
  openedAt: string;
  roundCount: number;
  openedBy: string;
  openedByWaiterName: string | null;
  runningTotal: string;
  pendingValidation: PendingValidationRound[];
  lastRoundAt: string | null;
}

interface RawWaiterTakeawayNote {
  uuid: string;
  kind: 'takeaway';
  label: string;
  label_provisional?: boolean;
  covers: number;
  opened_at: string;
  round_count: number;
  opened_by: string;
  opened_by_waiter_name: string | null;
  running_total: string;
  pending_validation: RawPendingValidationRound[];
  last_round_at: string | null;
}

function transformTakeawayNote(raw: RawWaiterTakeawayNote): WaiterTakeawayNote {
  return {
    uuid: raw.uuid,
    kind: raw.kind,
    label: raw.label,
    labelProvisional: raw.label_provisional === true,
    covers: raw.covers,
    openedAt: raw.opened_at,
    roundCount: raw.round_count,
    openedBy: raw.opened_by,
    openedByWaiterName: raw.opened_by_waiter_name,
    runningTotal: raw.running_total,
    pendingValidation: transformPendingValidation(raw.pending_validation),
    lastRoundAt: raw.last_round_at,
  };
}

export interface OpenJoinTakeawayResult {
  guestToken: string;
  session: WaiterTakeawayNote;
}

interface RawOpenJoinTakeawayResponse {
  guest_token: string;
  session: RawWaiterTakeawayNote;
}

// ---------------------------------------------------------------------------
// Session detail (contract §2 `GET sessions/<uuid>/`) — every OrderItem of
// every round of the note/table, for the Commandes view (F4) and decrement.
// `options` mirrors the shape `tableSessionApi.ts`'s `OrderLine.options`
// already uses for the guest-facing equivalent.
// ---------------------------------------------------------------------------

export interface WaiterOrderItemOption {
  name?: string;
  label?: string;
  qty?: number;
}

export interface WaiterOrderItem {
  uuid: string;
  productUuid: string;
  name: string;
  qte: number;
  unitTtc: string;
  totalTtc: string;
  roundNumber: number;
  options: WaiterOrderItemOption[];
  comment: string;
  isCartOption: boolean;
  addedByWaiter: boolean;
  category: string;
  /**
   * DF-0233 — ce plat est-il deja sorti en cuisine ? « Partir » efface la table
   * sans distinguer une table vide d'une table preparee et servie : mesure prod
   * du 2026-09-13, 32 annulations sur 32 portaient un bon de cuisine deja sorti,
   * 21 plus de 20 min apres l'envoi. La confirmation le dit maintenant.
   */
  sentToKitchen: boolean;
}

interface RawWaiterOrderItem {
  uuid: string;
  product_uuid: string;
  name: string;
  qte: number;
  unit_ttc: string;
  total_ttc: string;
  round_number: number;
  options: WaiterOrderItemOption[];
  comment?: string;
  is_cart_option: boolean;
  added_by_waiter: boolean;
  category: string;
  sent_to_kitchen?: boolean;
}

function transformOrderItem(raw: RawWaiterOrderItem): WaiterOrderItem {
  return {
    uuid: raw.uuid,
    productUuid: raw.product_uuid,
    name: raw.name,
    qte: raw.qte,
    unitTtc: raw.unit_ttc,
    totalTtc: raw.total_ttc,
    roundNumber: raw.round_number,
    options: raw.options ?? [],
    comment: raw.comment ?? '',
    isCartOption: raw.is_cart_option === true,
    addedByWaiter: raw.added_by_waiter === true,
    category: raw.category,
    sentToKitchen: raw.sent_to_kitchen === true,
  };
}

export interface WaiterOrder {
  uuid: string;
  onlineStatus: string;
  paymentStatus: string;
  caisseHandoffStatus: string;
  /** SC-787 — highest round of this anchor already confirmed (backend
   *  SC-356 `last_validated_round_number`); `null` = none yet. A table
   *  merges every round into one order, so this is the only thing that says
   *  WHICH rounds a `pending_validation` anchor is actually waiting on. */
  lastValidatedRoundNumber: number | null;
  totalTtc: string;
  items: WaiterOrderItem[];
  /** SC-902 — design §1.4: round numbers created by a "par service" round
   *  but not yet fired to the kitchen. `[]` on an older server — behaves
   *  exactly as before, nothing held. */
  heldRounds: number[];
  /** SC-902 — design §1.3: the frozen service label per round number, e.g.
   *  `{"3": "Plats"}` (renaming a service later does not rewrite history).
   *  `{}` on an older server. */
  roundServiceLabels: Record<string, string>;
  /** DF-0319 — unités retirées par le « − » sur cette commande (journal
   *  serveur). Une commande vidée n'a plus d'`items` : c'est la seule chose
   *  qui la distingue d'une note jamais remplie. `0` sur un serveur plus ancien. */
  removedQty?: number;
}

interface RawWaiterOrder {
  uuid: string;
  online_status: string;
  payment_status: string;
  caisse_handoff_status: string;
  last_validated_round_number?: number | null;
  total_ttc: string;
  items: RawWaiterOrderItem[];
  held_rounds?: number[];
  round_service_labels?: Record<string, string>;
  removed_qty?: number;
}

function transformOrder(raw: RawWaiterOrder): WaiterOrder {
  return {
    uuid: raw.uuid,
    onlineStatus: raw.online_status,
    paymentStatus: raw.payment_status,
    caisseHandoffStatus: raw.caisse_handoff_status,
    lastValidatedRoundNumber: raw.last_validated_round_number ?? null,
    totalTtc: raw.total_ttc,
    items: (raw.items ?? []).map(transformOrderItem),
    heldRounds: Array.isArray(raw.held_rounds)
      ? raw.held_rounds.filter((n): n is number => typeof n === 'number')
      : [],
    roundServiceLabels:
      raw.round_service_labels && typeof raw.round_service_labels === 'object'
        ? raw.round_service_labels
        : {},
    removedQty: typeof raw.removed_qty === 'number' ? raw.removed_qty : 0,
  };
}

export interface WaiterSessionDetail {
  session: WaiterTableSession;
  orders: WaiterOrder[];
  runningTotal: string;
  /** DF-0350 — ce qu'un envoi en caisse emporterait (les commandes, plus le
   *  forfait buffet là où le restaurant l'envoie). `null` = le serveur ne sait
   *  pas le dire ; absent d'un serveur plus ancien. */
  caisseTransferTotal: string | null;
}

interface RawWaiterSessionDetail {
  session: RawWaiterTableSession;
  orders: RawWaiterOrder[];
  running_total: string;
  caisse_transfer_total?: string | null;
}

function transformSessionDetail(raw: RawWaiterSessionDetail): WaiterSessionDetail {
  return {
    session: transformSession(raw.session),
    orders: (raw.orders ?? []).map(transformOrder),
    runningTotal: raw.running_total,
    caisseTransferTotal: raw.caisse_transfer_total ?? null,
  };
}

// ---------------------------------------------------------------------------
// Online orders (design §2.3/§3, B3 backend slice) — a CUSTOMER take-out or
// delivery order (`Order.table_session IS NULL`), distinct from an Emporter
// waiter note (`WaiterTakeawayNote` above). The list row omits `items`; the
// detail/status-change responses carry the fuller `WaiterOnlineOrderDetail`
// shape — its item projection is intentionally smaller than
// `WaiterOrderItem` (no `productUuid`/`roundNumber`/`addedByWaiter`/
// `category`: the online-order screen never posts a round or decrements a
// line, it only displays and changes `online_status`).
// ---------------------------------------------------------------------------

export interface WaiterOnlineOrder {
  uuid: string;
  dailyNumber: number;
  type: 'take_out' | 'delivery';
  consumerName: string;
  customerPhone: string;
  scheduledAt: string | null;
  /** SC-1500 — le client a choisi « Dès que possible » (`scheduledAt` = créneau le plus proche). */
  isAsap?: boolean;
  onlineStatus: string;
  paymentStatus: string;
  paymentChannel: string;
  totalTtc: string;
  itemsCount: number;
  customerNote: string;
  createdAt: string;
  // SC-948 — la réponse de la caisse à un « Envoyer en caisse » (null = jamais
  // envoyée). Optionnels comme ceux de `WaiterTableSession` : un serveur
  // antérieur ne les envoie pas.
  caisseHandoffStatus?: 'requested' | 'imported' | 'failed' | null;
  caisseHandoffError?: string;
}

interface RawWaiterOnlineOrder {
  uuid: string;
  daily_number: number;
  type: 'take_out' | 'delivery';
  consumer_name: string;
  customer_phone: string;
  scheduled_at: string | null;
  is_asap?: boolean;
  online_status: string;
  payment_status: string;
  payment_channel: string;
  total_ttc: string;
  items_count: number;
  customer_note: string;
  created_at: string;
  caisse_handoff_status?: 'requested' | 'imported' | 'failed' | null;
  caisse_handoff_error?: string;
}

function transformOnlineOrder(raw: RawWaiterOnlineOrder): WaiterOnlineOrder {
  return {
    uuid: raw.uuid,
    dailyNumber: raw.daily_number,
    type: raw.type,
    consumerName: raw.consumer_name,
    customerPhone: raw.customer_phone,
    scheduledAt: raw.scheduled_at,
    ...(raw.is_asap ? { isAsap: true } : {}),
    onlineStatus: raw.online_status,
    paymentStatus: raw.payment_status,
    paymentChannel: raw.payment_channel,
    totalTtc: raw.total_ttc,
    itemsCount: raw.items_count,
    customerNote: raw.customer_note ?? '',
    createdAt: raw.created_at,
    caisseHandoffStatus: raw.caisse_handoff_status ?? null,
    caisseHandoffError: raw.caisse_handoff_error ?? '',
  };
}

// ---------------------------------------------------------------------------
// SC-966 — today's reservations (`reservations/`). They live in ignite-hub;
// sale_api relays them for the waiter token (hub never sees that token), so
// the handheld only ever reads its own restaurant's bookings.
// ---------------------------------------------------------------------------

/** The moves the handheld can make: arrived, no-show, and the two faces of the
 *  undo — back to CONFIRMED, or back to CANCELLED for a booking that had been
 *  cancelled and was seated anyway (SC-970). The handheld never *creates* a
 *  cancellation: hub answers 409 NEVER_CANCELLED if it tries. */
export type WaiterReservationStatus = 'COMPLETED' | 'NO_SHOW' | 'CONFIRMED' | 'CANCELLED';

export interface WaiterReservation {
  uuid: string;
  reservedFor: string;
  partySize: number;
  customerName: string;
  customerPhone: string;
  notes: string;
  status: string;
  /** SC-970 — set as long as the booking was cancelled at some point, even
   *  after the waiter seated the guest anyway. `null` on a booking nobody
   *  cancelled; absent on a server predating SC-970 (read as `null`). */
  cancelledAt: string | null;
}

export interface WaiterReservationsDay {
  date: string;
  /** The restaurant's reservation module is on. Off ⇒ no tab at all. */
  enabled: boolean;
  /** Minutes a booking stays listed after its time (a late guest). */
  graceMinutes: number;
  reservations: WaiterReservation[];
}

/** SC-1612 — `YYYY-MM-DD` → combien de réservations et de personnes ; un
 *  jour sans réservation est absent. */
export type WaiterReservationCounts = Record<string, { count: number; covers: number }>;

interface RawWaiterReservation {
  uuid: string;
  reserved_for: string;
  party_size: number;
  customer_name: string;
  customer_phone: string;
  notes: string;
  status: string;
  cancelled_at?: string | null;
}

interface RawWaiterReservationsDay {
  date: string;
  enabled: boolean;
  grace_minutes: number;
  reservations: RawWaiterReservation[];
}

// ---------------------------------------------------------------------------
// Fidélité — « Scanner » (`loyalty/…`). Le programme vit dans ignite-hub ;
// sale_api relaie le scan pour le jeton serveur, avec le restaurant du jeton.
// Mêmes réponses que le scanner owner/admin (hub `loyalty/counter/`).
// ---------------------------------------------------------------------------

export type WaiterLoyaltyRewardType = 'percent' | 'fixed' | 'free_item' | 'free_combo' | 'custom';

export interface WaiterLoyaltyMember {
  displayName: string;
  balance: number;
}

/** Un avantage déjà débloqué, dans le portefeuille du client. */
export interface WaiterLoyaltyCoupon {
  code: string;
  rewardName: string;
  rewardType: WaiterLoyaltyRewardType;
  /** '5.00' — null pour une récompense qui n'est pas une remise. */
  value: string | null;
  expiresAt: string | null;
}

/** Une récompense du catalogue, à offrir avec les points du client. */
export interface WaiterLoyaltyReward {
  uuid: string;
  name: string;
  type: WaiterLoyaltyRewardType;
  value: string | null;
  pointsCost: number;
  affordable: boolean;
}

export type WaiterLoyaltyLookup =
  | {
      kind: 'member';
      cardCode: string;
      member: WaiterLoyaltyMember;
      rewards: WaiterLoyaltyReward[];
      coupons: WaiterLoyaltyCoupon[];
      /** Programme en marche. Éteint ⇒ on montre le client, sans rien offrir. */
      enabled: boolean;
    }
  | {
      kind: 'coupon';
      valid: true;
      coupon: WaiterLoyaltyCoupon;
      member: WaiterLoyaltyMember | null;
    }
  | {
      kind: 'coupon';
      valid: false;
      /** 'already_used' | 'expired' | … */
      reason: string;
      redeemedAt: string | null;
      member: WaiterLoyaltyMember | null;
    };

/** Ce que l'écran de succès doit dire : quoi retirer de l'addition. */
export interface WaiterLoyaltyRedeemed {
  rewardName: string;
  rewardType: WaiterLoyaltyRewardType;
  value: string | null;
  /** Solde après « Offrir » ; null après « Valider » (aucun point bouge). */
  balance: number | null;
}

/** SC-1638 — une addition clôturée que le serveur peut créditer sur la carte du client. */
export interface WaiterLoyaltyBill {
  sessionUuid: string;
  kind: 'dine_in_table' | 'takeaway';
  /** Numéro de table (« 5+6 » pour une tablée) ou nom d'une note à emporter. */
  label: string;
  /** Toujours clôturée : une note ouverte n'a pas encore de montant définitif. */
  status: 'closed';
  /** '42.50' — le total de l'addition, calculé par sale_api. */
  amount: string;
  closedAt: string | null;
}

/** Réponse de « Créditer » ; `alreadyCredited` = cette carte avait déjà cette addition. */
export interface WaiterLoyaltyCredited {
  credited: number;
  balance: number;
  alreadyCredited: boolean;
  /** '42.50' — le montant crédité, relu par sale_api au moment du crédit. */
  amount: string | null;
}

export interface WaiterLoyaltyCreditCancelled {
  cancelled: number;
  balance: number;
}

interface RawLoyaltyMember {
  display_name: string;
  balance: number;
}

interface RawLoyaltyCoupon {
  code: string;
  reward_name: string;
  reward_type: WaiterLoyaltyRewardType;
  value: string | null;
  expires_at: string | null;
}

interface RawLoyaltyReward {
  uuid: string;
  name: string;
  type: WaiterLoyaltyRewardType;
  value: string | null;
  points_cost: number;
  affordable: boolean;
}

interface RawLoyaltyLookup {
  kind: 'member' | 'coupon';
  card_code?: string;
  member?: RawLoyaltyMember | null;
  rewards?: RawLoyaltyReward[];
  coupons?: RawLoyaltyCoupon[];
  enabled?: boolean;
  valid?: boolean;
  reason?: string;
  redeemed_at?: string | null;
  code?: string;
  reward_name?: string;
  reward_type?: WaiterLoyaltyRewardType;
  value?: string | null;
  expires_at?: string | null;
}

interface RawLoyaltyRedeemed {
  reward_name: string;
  reward_type: WaiterLoyaltyRewardType;
  value: string | null;
  balance?: number;
}

interface RawLoyaltyBill {
  session_uuid: string;
  kind: 'dine_in_table' | 'takeaway';
  label: string;
  status: 'closed';
  amount: string;
  closed_at: string | null;
}

interface RawLoyaltyCredited {
  credited: number;
  balance: number;
  already_credited: boolean;
  amount?: string;
}

function transformLoyaltyMember(raw: RawLoyaltyMember | null | undefined): WaiterLoyaltyMember | null {
  if (!raw) return null;
  return { displayName: raw.display_name, balance: raw.balance };
}

function transformLoyaltyCoupon(raw: RawLoyaltyCoupon): WaiterLoyaltyCoupon {
  return {
    code: raw.code,
    rewardName: raw.reward_name,
    rewardType: raw.reward_type,
    value: raw.value ?? null,
    expiresAt: raw.expires_at ?? null,
  };
}

function transformLoyaltyLookup(raw: RawLoyaltyLookup): WaiterLoyaltyLookup {
  if (raw.kind === 'member') {
    return {
      kind: 'member',
      cardCode: raw.card_code ?? '',
      member: transformLoyaltyMember(raw.member) ?? { displayName: '', balance: 0 },
      rewards: (raw.rewards ?? []).map((r) => ({
        uuid: r.uuid,
        name: r.name,
        type: r.type,
        value: r.value ?? null,
        pointsCost: r.points_cost,
        affordable: r.affordable === true,
      })),
      coupons: (raw.coupons ?? []).map(transformLoyaltyCoupon),
      enabled: raw.enabled === true,
    };
  }
  const member = transformLoyaltyMember(raw.member);
  if (raw.valid === true) {
    return {
      kind: 'coupon',
      valid: true,
      coupon: transformLoyaltyCoupon({
        code: raw.code ?? '',
        reward_name: raw.reward_name ?? '',
        reward_type: raw.reward_type ?? 'custom',
        value: raw.value ?? null,
        expires_at: raw.expires_at ?? null,
      }),
      member,
    };
  }
  return {
    kind: 'coupon',
    valid: false,
    reason: raw.reason ?? 'not_found',
    redeemedAt: raw.redeemed_at ?? null,
    member,
  };
}

function transformLoyaltyRedeemed(raw: RawLoyaltyRedeemed): WaiterLoyaltyRedeemed {
  return {
    rewardName: raw.reward_name,
    rewardType: raw.reward_type,
    value: raw.value ?? null,
    balance: typeof raw.balance === 'number' ? raw.balance : null,
  };
}

function transformLoyaltyBill(raw: RawLoyaltyBill): WaiterLoyaltyBill {
  return {
    sessionUuid: raw.session_uuid,
    kind: raw.kind,
    label: raw.label,
    status: raw.status,
    amount: raw.amount,
    closedAt: raw.closed_at ?? null,
  };
}

function transformReservation(raw: RawWaiterReservation): WaiterReservation {
  return {
    uuid: raw.uuid,
    reservedFor: raw.reserved_for,
    partySize: raw.party_size,
    customerName: raw.customer_name,
    customerPhone: raw.customer_phone ?? '',
    notes: raw.notes ?? '',
    status: raw.status,
    cancelledAt: raw.cancelled_at ?? null,
  };
}

export interface WaiterOnlineOrderItem {
  uuid: string;
  name: string;
  qte: number;
  unitTtc: string;
  totalTtc: string;
  options: WaiterOrderItemOption[];
  comment: string;
  isCartOption: boolean;
}

interface RawWaiterOnlineOrderItem {
  uuid: string;
  name: string;
  qte: number;
  unit_ttc: string;
  total_ttc: string;
  options: WaiterOrderItemOption[];
  comment?: string;
  is_cart_option: boolean;
}

function transformOnlineOrderItem(raw: RawWaiterOnlineOrderItem): WaiterOnlineOrderItem {
  return {
    uuid: raw.uuid,
    name: raw.name,
    qte: raw.qte,
    unitTtc: raw.unit_ttc,
    totalTtc: raw.total_ttc,
    options: raw.options ?? [],
    comment: raw.comment ?? '',
    isCartOption: raw.is_cart_option === true,
  };
}

export interface WaiterOnlineOrderDetail extends WaiterOnlineOrder {
  items: WaiterOnlineOrderItem[];
}

interface RawWaiterOnlineOrderDetail extends RawWaiterOnlineOrder {
  items: RawWaiterOnlineOrderItem[];
}

function transformOnlineOrderDetail(raw: RawWaiterOnlineOrderDetail): WaiterOnlineOrderDetail {
  return {
    ...transformOnlineOrder(raw),
    items: (raw.items ?? []).map(transformOnlineOrderItem),
  };
}

export type OnlineOrderStatusTransition = 'preparing' | 'ready' | 'handed_over';

// ---------------------------------------------------------------------------
// Decrement (contract §2 `POST orders/<o>/items/<i>/decrement/`).
// ---------------------------------------------------------------------------

export interface DecrementItemResult {
  order: WaiterOrder;
  itemUuid: string;
  qteAfter: number;
  adjustmentUuid: string;
}

interface RawDecrementItemResponse {
  order: RawWaiterOrder;
  item_uuid: string;
  qte_after: number;
  adjustment_uuid: string;
}

function transformDecrementResult(raw: RawDecrementItemResponse): DecrementItemResult {
  return {
    order: transformOrder(raw.order),
    itemUuid: raw.item_uuid,
    qteAfter: raw.qte_after,
    adjustmentUuid: raw.adjustment_uuid,
  };
}

// ---------------------------------------------------------------------------
// Endpoints — every waiter-mode call sets its OWN explicit Authorization
// header (empty for the AllowAny unlock/, `Bearer <waiterToken>` for the rest)
// so the saleApi baseQuery's consumer-Token guard (`!headers.has('Authorization')`)
// never overwrites it — same escape tableSessionApi.ts uses for guest-Bearer
// calls. No change to saleApi.ts's allowlist needed (verified against
// fetchBaseQuery: headers passed into a queryFn's baseQuery() call are the
// same object handed to prepareHeaders, so `.has()` already sees them).
// ---------------------------------------------------------------------------

export const waiterApi = saleApi.injectEndpoints({
  endpoints: (build) => ({
    waiterPinPolicy: build.query<WaiterPinPolicy, string>({
      queryFn: async (restaurantUuid, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/pin-policy/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}`,
          // AllowAny endpoint — never send the consumer Token.
          headers: { Authorization: '' },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { pin_length?: number | null };
        return { data: { pinLength: raw.pin_length ?? null } };
      },
    }),

    unlock: build.mutation<UnlockResult, UnlockArgs>({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const body: Record<string, unknown> = {
          restaurant_uuid: args.restaurantUuid,
          pin: args.pin,
          device_id: args.deviceId,
        };
        if (args.deviceLabel) body.device_label = args.deviceLabel;
        if (args.waiterName) body.waiter_name = args.waiterName;
        if (args.takeoverDeviceUuid) body.takeover_device_uuid = args.takeoverDeviceUuid;
        if (args.forceNameTakeover) body.force_name_takeover = true;

        const result = await baseQuery({
          url: '/sale/order/waiter-mode/unlock/',
          method: 'POST',
          // AllowAny endpoint — never send the consumer Token.
          headers: { Authorization: '' },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: transformUnlock(result.data as RawUnlockResponse) };
      },
    }),

    /** SC-1044 — la clé applicative VAPID. `enabled: false` = la plateforme
     *  n'a pas de clé : le carnet cache la ligne « Notifications » plutôt que
     *  d'offrir un bouton dont on sait qu'il échouera. */
    pushKey: build.query<{ enabled: boolean; publicKey: string }, { token: string }>({
      queryFn: async ({ token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/push/key/',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { enabled?: boolean; public_key?: string };
        return { data: { enabled: !!raw.enabled, publicKey: raw.public_key ?? '' } };
      },
    }),

    subscribePush: build.mutation<void, { token: string; subscription: PushSubscriptionJSON }>({
      queryFn: async ({ token, subscription }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/push/subscribe/',
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { subscription },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    unsubscribePush: build.mutation<void, { token: string; endpoint?: string }>({
      queryFn: async ({ token, endpoint }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/push/unsubscribe/',
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: endpoint ? { endpoint } : {},
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    signOut: build.mutation<void, { token: string }>({
      queryFn: async ({ token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/sign-out/',
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    renewToken: build.mutation<RenewResult, { token: string }>({
      queryFn: async ({ token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/renew/',
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { token: string; expires_at: string };
        return { data: { token: raw.token, expiresAt: raw.expires_at } };
      },
    }),

    waiterTables: build.query<WaiterTable[], { restaurantUuid: string; token: string }>({
      queryFn: async ({ restaurantUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/tables/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: (result.data as RawWaiterTable[]).map(transformTable) };
      },
      providesTags: ['WaiterTables'],
    }),

    openJoinTable: build.mutation<
      OpenJoinTableResult,
      // SC-976 — design §5.2/D12: `extraTableUuids` is optional and additive.
      // A plain "Ouvrir" tap never sets it, so the request body is untouched
      // for the habitué (D12 — no existing test changes).
      { tableUuid: string; token: string; covers?: number; extraTableUuids?: string[] }
    >({
      queryFn: async ({ tableUuid, token, covers, extraTableUuids }, _api, _extra, baseQuery) => {
        const body: Record<string, unknown> = {};
        if (covers !== undefined) body.covers = covers;
        if (extraTableUuids && extraTableUuids.length > 0) body.extra_table_uuids = extraTableUuids;
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/tables/${tableUuid}/session/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as RawOpenJoinTableResponse;
        return { data: { guestToken: raw.guest_token, session: transformSession(raw.session) } };
      },
    }),

    // ---------------------------------------------------------------------
    // Salles (contract §3 `areas/`) + table CRUD (`tables/`, `tables/<uuid>/`)
    // — spec §2.2 D4 « Gérer les tables ».
    // ---------------------------------------------------------------------

    waiterAreas: build.query<WaiterArea[], { restaurantUuid: string; token: string }>({
      queryFn: async ({ restaurantUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/areas/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: (result.data as RawWaiterArea[]).map(transformArea) };
      },
      providesTags: ['WaiterAreas'],
    }),

    // 409 AREA_NAME_IN_USE (casefold, among active salles).
    createArea: build.mutation<WaiterArea, { name: string; color?: string; token: string }>({
      queryFn: async ({ name, color, token }, _api, _extra, baseQuery) => {
        const body: Record<string, unknown> = { name };
        if (color) body.color = color;
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/areas/',
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: transformArea(result.data as RawWaiterArea) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterAreas']),
    }),

    // 400 {number:[…]} (empty, > 26, already used incl. archived),
    // {capacity:[…]} (1..30), {area_uuid:[…]} (unknown / other restaurant).
    createTable: build.mutation<
      WaiterTable,
      { number: string; capacity: number; areaUuid?: string | null; token: string }
    >({
      queryFn: async ({ number, capacity, areaUuid, token }, _api, _extra, baseQuery) => {
        const body: Record<string, unknown> = { number, capacity };
        if (areaUuid !== undefined) body.area_uuid = areaUuid;
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/tables/',
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: transformTable(result.data as RawWaiterTable) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterTables']),
    }),

    // `area_uuid: null` clears the salle assignment ("Sans salle").
    updateTable: build.mutation<
      WaiterTable,
      { tableUuid: string; number?: string; capacity?: number; areaUuid?: string | null; token: string }
    >({
      queryFn: async ({ tableUuid, number, capacity, areaUuid, token }, _api, _extra, baseQuery) => {
        const body: Record<string, unknown> = {};
        if (number !== undefined) body.number = number;
        if (capacity !== undefined) body.capacity = capacity;
        if (areaUuid !== undefined) body.area_uuid = areaUuid;
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/tables/${tableUuid}/`,
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: transformTable(result.data as RawWaiterTable) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterTables']),
    }),

    // 409 SESSION_OPEN — the table has an OPEN session, close it first.
    archiveTable: build.mutation<ArchiveTableResult, { tableUuid: string; token: string }>({
      queryFn: async ({ tableUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/tables/${tableUuid}/`,
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        const raw = (result.data ?? {}) as RawArchiveTableResult;
        return { data: { detail: raw.detail ?? '', archived: raw.archived === true } };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterTables']),
    }),

    // ---------------------------------------------------------------------
    // Commandes en ligne (contract §3 `online-orders/`) — spec §2.3 D7,
    // customer take-out/delivery orders, never the Emporter waiter notes
    // above.
    // ---------------------------------------------------------------------

    onlineOrders: build.query<WaiterOnlineOrder[], { restaurantUuid: string; token: string }>({
      queryFn: async ({ restaurantUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: (result.data as RawWaiterOnlineOrder[]).map(transformOnlineOrder) };
      },
      providesTags: ['OnlineOrders'],
    }),

    onlineOrderDetail: build.query<WaiterOnlineOrderDetail, { orderUuid: string; token: string }>({
      queryFn: async ({ orderUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformOnlineOrderDetail(result.data as RawWaiterOnlineOrderDetail) };
      },
      providesTags: ['OnlineOrders'],
    }),

    // 400 INVALID_TRANSITION — `apply_online_status_change`'s matrix refused
    // the requested `online_status`.
    setOnlineOrderStatus: build.mutation<
      WaiterOnlineOrderDetail,
      { orderUuid: string; onlineStatus: OnlineOrderStatusTransition; token: string }
    >({
      queryFn: async ({ orderUuid, onlineStatus, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/status/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { online_status: onlineStatus },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformOnlineOrderDetail(result.data as RawWaiterOnlineOrderDetail) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['OnlineOrders']),
    }),

    // SC-948 — envoi d'une commande en ligne vers la caisse liée, comme au
    // back-office. 202 = mis en file seulement : la caisse répond ensuite
    // (SSE `order_updated` → tag `OnlineOrders`). 400 `SELECT_CAISSE` sans
    // caisse, 409 `ALREADY_REQUESTED`/`ALREADY_IMPORTED` si elle est déjà
    // partie — d'où le rafraîchissement même en erreur : l'état a bougé ailleurs.
    handoffOnlineOrderToCaisse: build.mutation<unknown, { orderUuid: string; token: string }>({
      queryFn: async ({ orderUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/handoff-to-caisse/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: result.data };
      },
      invalidatesTags: ['OnlineOrders'],
    }),

    // SC-960 — « Terminer sans passer par la caisse » : la commande est remise
    // au client sans transiter par la caisse. Le serveur parcourt la matrice
    // jusqu'à `handed_over`, donc le geste marche quel que soit l'avancement.
    finishOnlineOrder: build.mutation<WaiterOnlineOrderDetail, { orderUuid: string; token: string }>({
      queryFn: async ({ orderUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/finish/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: transformOnlineOrderDetail(result.data as RawWaiterOnlineOrderDetail) };
      },
      invalidatesTags: ['OnlineOrders'],
    }),

    // SC-960 — « Partir » sur une commande web : le client est parti, la
    // commande est annulée. Elle quitte alors la liste du carnet et reste
    // lisible en `Annulée` côté admin et owner. 409 CAISSE_HANDOFF_ACTIVE
    // quand la caisse la tient déjà.
    cancelOnlineOrder: build.mutation<WaiterOnlineOrderDetail, { orderUuid: string; token: string }>({
      queryFn: async ({ orderUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/cancel/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: transformOnlineOrderDetail(result.data as RawWaiterOnlineOrderDetail) };
      },
      invalidatesTags: ['OnlineOrders'],
    }),

    // SC-960 — « Imprimer » sur une commande web. Même forme que
    // `printSessionTotal` (202 « mis en file » + `dispatch_uuid` à suivre par
    // `printTotalStatus`) : c'est le même geste sur une commande sans session.
    // N'invalide rien — imprimer ne change pas l'état de la commande.
    printOnlineOrderReceipt: build.mutation<PrintTotalQueued, { orderUuid: string; token: string }>({
      queryFn: async ({ orderUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/print-receipt/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { destination?: string; dispatch_uuid?: string };
        return {
          data: {
            destination: (raw?.destination ?? '') as PrintTotalDestination,
            dispatchUuid: raw?.dispatch_uuid ?? '',
          },
        };
      },
    }),

    // ---------------------------------------------------------------------
    // SC-966 — today's reservations (relayed from ignite-hub by sale_api).
    // ---------------------------------------------------------------------

    // SC-1612 — `date` (`YYYY-MM-DD`) lists another day ; absent = today.
    waiterReservations: build.query<
      WaiterReservationsDay,
      { restaurantUuid: string; token: string; date?: string }
    >({
      queryFn: async ({ restaurantUuid, token, date }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url:
            `/sale/order/waiter-mode/reservations/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}` +
            (date ? `&date=${encodeURIComponent(date)}` : ''),
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as RawWaiterReservationsDay;
        return {
          data: {
            date: raw.date,
            enabled: raw.enabled === true,
            graceMinutes: raw.grace_minutes,
            reservations: (raw.reservations ?? []).map(transformReservation),
          },
        };
      },
      providesTags: ['WaiterReservations'],
    }),

    // SC-1612 — le calendrier du carnet : réservations + personnes par jour
    // local, `from`/`to` inclus. Même étiquette que la liste : une
    // réservation créée, déplacée ou annulée recompte le calendrier.
    waiterReservationCounts: build.query<WaiterReservationCounts, { token: string; from: string; to: string }>({
      query: ({ token, from, to }) => ({
        url: `/sale/order/waiter-mode/reservations/counts/?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: (raw: { days?: WaiterReservationCounts }) => raw.days ?? {},
      providesTags: ['WaiterReservations'],
    }),

    // SC-971 — le client appelle, le serveur inscrit. Pas de `restaurant_uuid`
    // dans le corps : sale_api prend celui du jeton. 400 = champs refusés par
    // hub, 409 RESERVATION_DISABLED = module fermé entre-temps, 502 = hub muet.
    createWaiterReservation: build.mutation<
      WaiterReservation,
      {
        token: string;
        customerName: string;
        partySize: number;
        reservedFor: string;
        customerPhone?: string;
        notes?: string;
      }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/reservations/',
          method: 'POST',
          headers: { Authorization: `Bearer ${args.token}` },
          body: {
            customer_name: args.customerName,
            party_size: args.partySize,
            reserved_for: args.reservedFor,
            customer_phone: args.customerPhone ?? '',
            notes: args.notes ?? '',
          },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformReservation(result.data as RawWaiterReservation) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterReservations']),
    }),

    // SC-1281 — le client rappelle : « on sera 6 », « plutôt 20h30 »,
    // « demain ». Mêmes champs que la création, tous renvoyés ; hub ne touche
    // que ce qui a changé. 409 NOT_EDITABLE = plus CONFIRMED (un autre appareil
    // l'a marquée), PAST_DATE = jour passé, RESERVATION_DISABLED = module fermé.
    updateWaiterReservation: build.mutation<
      WaiterReservation,
      {
        token: string;
        reservationUuid: string;
        customerName: string;
        partySize: number;
        reservedFor: string;
        customerPhone: string;
        notes: string;
      }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/reservations/${args.reservationUuid}/`,
          method: 'PATCH',
          headers: { Authorization: `Bearer ${args.token}` },
          body: {
            customer_name: args.customerName,
            party_size: args.partySize,
            reserved_for: args.reservedFor,
            customer_phone: args.customerPhone,
            notes: args.notes,
          },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformReservation(result.data as RawWaiterReservation) };
      },
      // Un refus aussi : NOT_EDITABLE veut dire que la liste est en retard.
      invalidatesTags: ['WaiterReservations'],
    }),

    // 409 {code: TOO_EARLY | NOT_TODAY | ALREADY_HANDLED | NEVER_CANCELLED |
    // WAS_CANCELLED} — hub refused the move; 502 HUB_UNAVAILABLE — sale_api
    // could not reach hub.
    setWaiterReservationStatus: build.mutation<
      WaiterReservation,
      { reservationUuid: string; status: WaiterReservationStatus; token: string }
    >({
      queryFn: async ({ reservationUuid, status, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/reservations/${reservationUuid}/status/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { status },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformReservation(result.data as RawWaiterReservation) };
      },
      // Refetch on a refusal too: ALREADY_HANDLED means another handheld got
      // there first, and the list should drop the row rather than keep it.
      invalidatesTags: ['WaiterReservations'],
    }),

    // SC-1532 — « Imprimer » dans la fiche : le ticket ressort là où sort le
    // ticket automatique (caisse ou imprimante cloud), avec RÉIMPRESSION.
    // 202 {destination}; 409 {code: NO_PRINT_DESTINATION} = le restaurant
    // imprime au navigateur du comptoir ; 409 NOT_PRINTABLE = annulée / pas
    // venue ; 404 RESERVATION_NOT_FOUND ; 502 HUB_UNAVAILABLE /
    // PRINT_ROUTING_UNREADABLE / PRINT_FAILED. Rien à rafraîchir : imprimer
    // ne change pas la réservation.
    printWaiterReservation: build.mutation<
      { destination: 'caisse' | 'cloud' },
      { reservationUuid: string; token: string }
    >({
      query: ({ reservationUuid, token }) => ({
        url: `/sale/order/waiter-mode/reservations/${reservationUuid}/print/`,
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: {},
      }),
    }),

    // ---------------------------------------------------------------------
    // Fidélité — « Scanner » (relayed from ignite-hub by sale_api). Three
    // mutations, the lookup included: « déjà utilisé » must be read fresh on
    // every scan, never from a cache. 404 {detail: not_found} = unknown code;
    // 400/403 {detail: <code>} = hub refused; 502 HUB_UNAVAILABLE = hub mute.
    // ---------------------------------------------------------------------

    lookupLoyaltyCode: build.mutation<WaiterLoyaltyLookup, { code: string; token: string }>({
      queryFn: async ({ code, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/loyalty/lookup/${encodeURIComponent(code)}/`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformLoyaltyLookup(result.data as RawLoyaltyLookup) };
      },
    }),

    // Une seule tentative (sale_api n'en refait pas non plus) : un renvoi
    // après une réponse perdue reviendrait « déjà utilisé ».
    redeemLoyaltyCoupon: build.mutation<WaiterLoyaltyRedeemed, { code: string; token: string }>({
      queryFn: async ({ code, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/loyalty/coupons/${encodeURIComponent(code)}/redeem/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: transformLoyaltyRedeemed(result.data as RawLoyaltyRedeemed) };
      },
    }),

    // Dépense les points : jamais rejoué automatiquement, hub n'a pas de clé
    // d'idempotence ici.
    offerLoyaltyReward: build.mutation<
      WaiterLoyaltyRedeemed,
      { cardCode: string; rewardUuid: string; token: string }
    >({
      queryFn: async ({ cardCode, rewardUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/loyalty/members/${encodeURIComponent(cardCode)}/redeem/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { reward_uuid: rewardUuid },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformLoyaltyRedeemed(result.data as RawLoyaltyRedeemed) };
      },
    }),

    // SC-1638 — « Créditer une addition » : les additions clôturées depuis
    // peu. La liste est relue à chaque client affiché
    // (`refetchOnMountOrArgChange` à l'appel) : une table a pu fermer entre
    // deux scans.
    listLoyaltyBills: build.query<WaiterLoyaltyBill[], { token: string }>({
      queryFn: async ({ token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/loyalty/bills/`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: (result.data as RawLoyaltyBill[]).map(transformLoyaltyBill) };
      },
    }),

    // Le téléphone n'envoie que l'addition choisie : sale_api la chiffre.
    // Rejouable sans risque (hub crédite une addition une fois par carte),
    // mais jamais rejoué d'ici : un second geste passe par la confirmation.
    creditLoyaltyBill: build.mutation<
      WaiterLoyaltyCredited,
      { cardCode: string; sessionUuid: string; token: string }
    >({
      queryFn: async ({ cardCode, sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/loyalty/members/${encodeURIComponent(cardCode)}/credit/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { session_uuid: sessionUuid },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as RawLoyaltyCredited;
        return {
          data: {
            credited: raw.credited,
            balance: raw.balance,
            alreadyCredited: raw.already_credited === true,
            amount: raw.amount ?? null,
          },
        };
      },
    }),

    // 30 min, tant que les points sont là (règle de hub). Une seule tentative :
    // un renvoi après une réponse perdue reviendrait « déjà annulé ».
    cancelLoyaltyCredit: build.mutation<
      WaiterLoyaltyCreditCancelled,
      { cardCode: string; sessionUuid: string; token: string }
    >({
      query: ({ cardCode, sessionUuid, token }) => ({
        url: `/sale/order/waiter-mode/loyalty/members/${encodeURIComponent(cardCode)}/credit/${encodeURIComponent(sessionUuid)}/cancel/`,
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: {},
      }),
    }),

    // ---------------------------------------------------------------------
    // Emporter notes (contract §2 `takeaway/`).
    // ---------------------------------------------------------------------

    listTakeaway: build.query<WaiterTakeawayNote[], { restaurantUuid: string; token: string }>({
      queryFn: async ({ restaurantUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/takeaway/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: (result.data as RawWaiterTakeawayNote[]).map(transformTakeawayNote) };
      },
      providesTags: ['Takeaway'],
    }),

    // 409 LABEL_IN_USE — a duplicate label (casefold, among OPEN notes only).
    // SC-1466 — `provisional: true` (« Plus tard ») : no label, the server
    // names the note « Client N ».
    createTakeaway: build.mutation<
      OpenJoinTakeawayResult,
      { label: string; token: string } | { provisional: true; token: string }
    >({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/takeaway/',
          method: 'POST',
          headers: { Authorization: `Bearer ${arg.token}` },
          body: 'provisional' in arg ? { provisional: true } : { label: arg.label },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as RawOpenJoinTakeawayResponse;
        return { data: { guestToken: raw.guest_token, session: transformTakeawayNote(raw.session) } };
      },
    }),

    // SC-1466 — name (or rename) an open note, typically after its order
    // went out under « Client N ». 409 LABEL_IN_USE / SESSION_NOT_OPEN.
    renameTakeaway: build.mutation<
      { label: string; labelProvisional: boolean },
      { noteUuid: string; label: string; token: string }
    >({
      queryFn: async ({ noteUuid, label, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/takeaway/${noteUuid}/`,
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
          body: { label },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { session: RawWaiterTakeawayNote };
        return { data: { label: raw.session.label, labelProvisional: raw.session.label_provisional === true } };
      },
      invalidatesTags: (_result, error, args) =>
        error ? [] : [{ type: 'SessionDetail', id: args.noteUuid }, 'Takeaway'],
    }),

    // Re-join an already-open note — open-or-join, never 409 on a normal tap
    // (409 SESSION_NOT_OPEN only if the note was closed meanwhile).
    joinTakeaway: build.mutation<OpenJoinTakeawayResult, { noteUuid: string; token: string }>({
      queryFn: async ({ noteUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/takeaway/${noteUuid}/session/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as RawOpenJoinTakeawayResponse;
        return { data: { guestToken: raw.guest_token, session: transformTakeawayNote(raw.session) } };
      },
    }),

    // ---------------------------------------------------------------------
    // Session detail + archivage (contract §2 `sessions/<uuid>/`, `finish/`,
    // `cancel/`) and per-item decrement (`orders/<o>/items/<i>/decrement/`).
    // ---------------------------------------------------------------------

    getSessionDetail: build.query<WaiterSessionDetail, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformSessionDetail(result.data as RawWaiterSessionDetail) };
      },
      providesTags: (_result, _error, arg) => [{ type: 'SessionDetail', id: arg.sessionUuid }],
    }),

    // Valider → confirm-round (contract §2 `POST /sale/order/order/online/`,
    // waiter branch — spec §1.7 D2/§2.5). Uses the SESSION's guest token, not
    // the waiter JWT: the round is authenticated exactly like a customer's
    // round, only "waiter"-flavoured (dine_in table session opened by staff,
    // or a takeaway note's guest token).
    //
    // Review finding (team-lead, F45 follow-up): this endpoint's 201 body is
    // `{order: OrderOutputSerializer, ...}` — the RAW model field names
    // (`unit_ttc_origin`, `total_ttc_after_discount`, `product_name`, …), NOT
    // the `GET sessions/<uuid>/` detail shape (`unit_ttc`, `total_ttc`,
    // `name`, `product_uuid`) `transformOrder`/`RawWaiterOrder` expect. Do
    // NOT transform it into a `WaiterOrder` here — only the order's uuid is
    // used, everything else is left to the `invalidatesTags` refetch below
    // (fires immediately on success, NOT the 5s poll — W-T-58's "without
    // waiting for a poll" still holds).
    postRound: build.mutation<
      { orderUuid: string },
      {
        sessionUuid: string;
        restaurantUuid: string;
        guestToken: string;
        type: 'dine_in' | 'take_out';
        lines: {
          product_uuid: string;
          quantity: number;
          options: { option_value_uuid: string; qty: number }[];
          comment: string;
          /** SC-902 — design §1.3: 1-based service index. Omitted (today's
           *  behaviour) → the wire body is byte-identical to before this
           *  field existed; every line must carry one, or none at all. */
          course?: number;
          /** SC-902 — design §1.10 rule A: joins an EXISTING held round of
           *  the anchor instead of creating a new one (a mid-meal addition
           *  for a service already retenu). Mutually exclusive with
           *  `course` on the same line (server: 400 `COURSES_PARTIAL`). */
          held_round?: number;
        }[];
        totalTtc: string;
        cartOptions?: { option_value_uuid: string; qty: number }[];
        /** SC-902 — frozen label per service, keyed by its string index
         *  (`{"1": "Entrées"}`), sent only alongside `course`-bearing lines. */
        courseLabels?: Record<string, string>;
        /** SC-902 — reviewer finding F1 / spec §1.10 « troisième forme » :
         *  every `course`-bearing round this request creates is held, none
         *  fires — the plan-has-no-`now`-entry case (a service added after
         *  every other service is already held). Omitted (today's
         *  behaviour, or a plan with a `now` entry) → wire body unchanged. */
        holdAll?: boolean;
        /** Staff PIN re-typed on a LINE_QTE_EXCEEDED / ORDER_QTE_EXCEEDED
         *  refusal — lifts the 20-per-line / 60-per-round caps for THIS round
         *  (a table of 30). Omitted → wire body unchanged. */
        staffPin?: string;
      }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        // Namespaced per SESSION (not per restaurant, unlike the guest
        // checkout's `idempotencyKey`) — spec §1.7: two open tickets on the
        // same restaurant must never collide on one key.
        // SC-1572 — after a 409 IDEMPOTENCY_CONFLICT the key is kept (see
        // below), so the very same cart re-sent comes back 409 again: safe,
        // the round that landed is not sent twice. But a cart the waiter has
        // since EDITED (dishes already gone removed, as the message asks) is a
        // new round of its own: keeping the key would refuse it forever — the
        // DF-0253 trap. A changed content therefore gets a fresh key.
        const contentSignature = JSON.stringify([
          args.lines,
          args.cartOptions ?? [],
          args.courseLabels ?? {},
          !!args.holdAll,
        ]);
        const conflictKey = `waiter-idempotency-conflict:${args.sessionUuid}`;
        const conflictedContent = sessionStorage.getItem(conflictKey);
        if (conflictedContent !== null && conflictedContent !== contentSignature) {
          clearIdempotencyKey(args.sessionUuid);
          sessionStorage.removeItem(conflictKey);
        }
        const idem = idempotencyKey(args.sessionUuid);
        // DF-0383 — tant que cet envoi n'a pas de réponse définitive, la copie
        // de secours du panier n'est proposée à aucun autre onglet : la clé
        // ci-dessus ne vit que dans celui-ci, un renvoi d'ailleurs ferait une
        // seconde tournée si la première est arrivée.
        markWaiterSendPending(args.sessionUuid, true);
        const result = await baseQuery({
          url: '/sale/order/order/online/',
          method: 'POST',
          headers: { Authorization: `Bearer ${args.guestToken}`, 'Idempotency-Key': idem },
          // A connection the server accepted but never answers (sale_api
          // restarting mid-request) otherwise leaves « Envoi… » spinning
          // forever. The key above survives a TIMEOUT_ERROR, so the waiter's
          // re-send replays the round if the first one did land.
          timeout: POST_ROUND_TIMEOUT_MS,
          body: {
            restaurant_uuid: args.restaurantUuid,
            type: args.type,
            payment_channel: 'on_site',
            // `course` stays absent on the wire (not sent as `undefined`)
            // when the caller doesn't set it — JSON.stringify drops
            // undefined-valued keys, so a call without courses produces the
            // exact body sent before this field existed.
            lines: args.lines,
            total_ttc: args.totalTtc,
            ...(args.cartOptions?.length ? { cart_options: args.cartOptions } : {}),
            ...(args.courseLabels && Object.keys(args.courseLabels).length > 0
              ? { course_labels: args.courseLabels }
              : {}),
            ...(args.holdAll ? { hold_all: true } : {}),
            ...(args.staffPin ? { staff_pin: args.staffPin } : {}),
            idempotency_key: idem,
          },
        });
        if (result.error) {
          // SC-902 — design §1.3: the `DineInIdempotency` claim is created
          // BEFORE the course gate runs server-side, so a refusal on that
          // gate must rotate the key — otherwise the waiter's corrected
          // re-send (same cart, no `course`) collides with the claim already
          // recorded for THIS key and comes back 409 IDEMPOTENCY_CONFLICT
          // instead of succeeding.
          //
          // DF-0253 — that reasoning was never specific to the course gate:
          // EVERY business refusal runs after the claim (qte caps, budget,
          // max-rounds, already-handed-off…), and rotating only on
          // `COURSES_*` left the others burning the key. Prod 2026-09-18 at
          // Yuji: a cart refused `ORDER_QTE_EXCEEDED`, then 17 × 409 on the
          // trimmed re-sends, table closed with nothing in the kitchen. The
          // key lives in `sessionStorage`, so F5 did not help — only closing
          // the tab did. sale_api now rolls the claim back on refusal; this
          // side rotates on any 4xx as well, so an old server (or a refusal
          // code added later) cannot re-open the trap.
          //
          // The line is HTTP status, not the code list: a 4xx means the
          // server decided nothing happened, so a fresh key is safe. A
          // timeout, a network drop or a 5xx are ambiguous — there the key
          // MUST survive, because it is the only thing preventing a double
          // round on the retry. `status` is a number only for a real HTTP
          // answer; RTK Query puts 'FETCH_ERROR' / 'TIMEOUT_ERROR' there
          // otherwise, which is exactly the ambiguous case.
          const httpStatus = (result.error as { status?: number | string })?.status;
          //
          // SC-1572 — the one 4xx that does NOT say « nothing happened »:
          // 409 IDEMPOTENCY_CONFLICT means the server already holds this key
          // for another content, i.e. an earlier send (timed out client-side)
          // DID land and the cart was then edited. A fresh key would re-send
          // that round (paper and bill twice) — the key stays, the screen
          // re-reads the session and tells the waiter to check the ticket.
          const errCode = (result.error as { data?: { code?: unknown } })?.data?.code;
          if (typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500) {
            if (httpStatus === 409 && errCode === 'IDEMPOTENCY_CONFLICT') {
              sessionStorage.setItem(conflictKey, contentSignature);
            } else {
              clearIdempotencyKey(args.sessionUuid);
              sessionStorage.removeItem(conflictKey);
            }
            markWaiterSendPending(args.sessionUuid, false);
          }
          return { error: result.error as never };
        }
        clearIdempotencyKey(args.sessionUuid);
        sessionStorage.removeItem(conflictKey);
        const raw = result.data as { order: { uuid: string } };
        return { data: { orderUuid: raw.order.uuid } };
      },
      invalidatesTags: (_result, error, args) =>
        error ? [] : [{ type: 'SessionDetail', id: args.sessionUuid }, 'WaiterTables', 'Takeaway'],
    }),

    // SC-902 — « Lancer » a held service round to the kitchen (design §1.6):
    // the waiter counterpart of the counter's `fire-round/`. Server-side
    // idempotent (a round already fired ⇒ 200, not an error), so retapping
    // on a slow network is safe.
    fireRound: build.mutation<
      WaiterOrder,
      { orderUuid: string; roundNumber: number; sessionUuid: string; token: string }
    >({
      queryFn: async ({ orderUuid, roundNumber, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/orders/${orderUuid}/rounds/${roundNumber}/fire/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: transformOrder(result.data as RawWaiterOrder) };
      },
      invalidatesTags: (_result, error, args) =>
        error ? [] : [{ type: 'SessionDetail', id: args.sessionUuid }],
    }),

    // « ✔ sans caisse » — 409 PENDING_ROUND_EXISTS (a QR round never reached
    // the kitchen: validate/refuse first) ; 409 ORDER_LOCKED (handoff requested).
    finishSession: build.mutation<void, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/finish/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    // « Partir » — force_cancel_session semantics (unpaid → cancelled, paid
    // kept). 409 CAISSE_HANDOFF_ACTIVE if the caisse already imported the unit.
    cancelSession: build.mutation<void, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/cancel/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    // « − » in Commandes — qty−1, line removed at 0. 409 ITEM_LOCKED
    // {reason: paid|handed_over|cancelled|handed_off|pending|session_closed} ;
    // 400 NOT_A_DISH (a cart-option line, not a dish).
    decrementItem: build.mutation<
      DecrementItemResult,
      { orderUuid: string; itemUuid: string; token: string }
    >({
      queryFn: async ({ orderUuid, itemUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/orders/${orderUuid}/items/${itemUuid}/decrement/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: transformDecrementResult(result.data as RawDecrementItemResponse) };
      },
    }),

    // S5 (sale-validate-refuse-close) is not built yet — these three declare
    // the contract from spec §2.3 ahead of time; F2 wires them into the UI.
    // SC-953 — `roundNumber` is the round the waiter was looking at: sale_api
    // answers 409 ROUND_ALREADY_HANDLED if a colleague got there first or a
    // newer round is now the one waiting.
    validateOrder: build.mutation<void, { orderUuid: string; roundNumber?: number; token: string }>({
      queryFn: async ({ orderUuid, roundNumber, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/orders/${orderUuid}/validate/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: roundNumber === undefined ? undefined : { round_number: roundNumber },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    refuseOrder: build.mutation<void, { orderUuid: string; roundNumber?: number; token: string }>({
      queryFn: async ({ orderUuid, roundNumber, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/orders/${orderUuid}/refuse/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: roundNumber === undefined ? undefined : { round_number: roundNumber },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    // SC-953 — « Modifier » : the round leaves the queue and its dishes come
    // back for the waiter's own cart.
    takeOverRound: build.mutation<
      TakenOverRound,
      { orderUuid: string; roundNumber: number; token: string }
    >({
      queryFn: async ({ orderUuid, roundNumber, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/orders/${orderUuid}/take-over/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { round_number: roundNumber },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformTakenOverRound(result.data as RawTakenOverRound) };
      },
    }),

    closeSession: build.mutation<void, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/close/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    // Spec 2026-08-28 paid-table-ordering-lock §3.3 — rouvrir la prise de
    // commande d'une table que le convive a soldee depuis son telephone.
    // Idempotent cote serveur : retaper le bouton ne produit pas d'erreur.
    unlockSessionOrdering: build.mutation<void, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/unlock-ordering/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),

    // SC-383 — 转台: move an open session to a FREE destination table. Wraps
    // the waiter-mode move endpoint (reusing the staff move service server-side);
    // 409 `{code: TARGET_TABLE_OCCUPIED | SESSION_NOT_OPEN | SESSION_EXPIRED}`.
    // SC-976 follow-up — `extraTableUuids`: the other tables of a multi-table
    // destination, attached in the same request (409 `TABLE_OCCUPIED`
    // +`table_number` / `TOO_MANY_TABLES`, 400 `ALREADY_ATTACHED`). Left out
    // of the body when empty so a single-table move stays byte-identical.
    transferSession: build.mutation<
      void,
      { sessionUuid: string; targetTableUuid: string; extraTableUuids?: string[]; token: string }
    >({
      queryFn: async ({ sessionUuid, targetTableUuid, extraTableUuids, token }, _api, _extra, baseQuery) => {
        const body: { target_table_uuid: string; extra_table_uuids?: string[] } = {
          target_table_uuid: targetTableUuid,
        };
        if (extraTableUuids && extraTableUuids.length > 0) body.extra_table_uuids = extraTableUuids;
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/move/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),
    // SC-958 — corriger le nombre de couverts d'une table ouverte, depuis la
    // même feuille ⋮ que le 转台. 400 `{code: covers_exceeds_capacity,
    // max_covers}` (même plafond que l'ouverture, SC-380), 409 `{code:
    // SESSION_NOT_OPEN | NOT_A_TABLE_SESSION}`.
    updateSessionCovers: build.mutation<void, { sessionUuid: string; covers: number; token: string }>({
      queryFn: async ({ sessionUuid, covers, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/covers/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { covers },
        });
        if (result.error) return { error: result.error as never };
        return { data: undefined };
      },
    }),
    // SC-976 — design §3.5/§5.1 « Ajouter une table » : rattache une table
    // LIBRE à une session déjà ouverte (n'importe laquelle des deux, la
    // même session côté serveur). 200 = la charge de session à jour
    // (`tableLabel`/`tableUuids`/`isMerged` déjà recalculés) ; 409
    // `TABLE_OCCUPIED`/`TOO_MANY_TABLES`, 400 `NOT_A_TABLE_SESSION` |
    // `ALREADY_ATTACHED` | `SESSION_NOT_OPEN`, 404 cross-tenant.
    attachTable: build.mutation<
      WaiterTableSession,
      { sessionUuid: string; tableUuid: string; token: string }
    >({
      queryFn: async ({ sessionUuid, tableUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/tables/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { table_uuid: tableUuid },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformSession(result.data as RawWaiterTableSession) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterTables']),
    }),

    // SC-976 — design §3.5/§5.1 « Séparer » : détache une table rattachée,
    // JAMAIS l'ancre (400 `CANNOT_DETACH_ANCHOR`, l'UI ne propose pas ce
    // geste sur l'ancre elle-même). 400 `NOT_ATTACHED` si la table donnée
    // n'est pas (ou plus) dans cette session.
    detachTable: build.mutation<
      WaiterTableSession,
      { sessionUuid: string; tableUuid: string; token: string }
    >({
      queryFn: async ({ sessionUuid, tableUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/tables/${tableUuid}/`,
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: transformSession(result.data as RawWaiterTableSession) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterTables']),
    }),

    // SC-976 regroup (2026-09-16) — the ⋮ sheet's « Transférer » (`move`: the
    // tablée's tables BECOME `tableUuids`, first = anchor, up to 8) and
    // « Réunir » (`merge`: added). A table of another note absorbs that whole
    // note. 200 = the session payload; 409 `{code: TOO_MANY_TABLES (+max) |
    // GUEST_PAYMENT_IN_FLIGHT | ORDER_AT_CAISSE | ROUND_TO_VALIDATE |
    // BUFFET_NOT_MERGEABLE | NOTE_HAS_PAYMENT (+table_number) | STALE |
    // SESSION_NOT_OPEN}`.
    regroupSession: build.mutation<
      WaiterTableSession,
      {
        sessionUuid: string;
        mode: 'move' | 'merge';
        tableUuids: string[];
        // Another note on a picked table has its bill merged in (SC-1007: no
        // « replace »). Left out of the body when no occupied table is involved.
        onOccupied?: 'merge';
        token: string;
      }
    >({
      queryFn: async ({ sessionUuid, mode, tableUuids, onOccupied, token }, _api, _extra, baseQuery) => {
        const body: { mode: string; table_uuids: string[]; on_occupied?: string } = { mode, table_uuids: tableUuids };
        if (onOccupied) body.on_occupied = onOccupied;
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/regroup/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        });
        if (result.error) return { error: result.error as never };
        return { data: transformSession(result.data as RawWaiterTableSession) };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['WaiterTables']),
    }),

    // Transfert manuel de la table vers la caisse liée : la caisse en fait une
    // commande encaissable et produit le ticket NF525. 400 `{code:SELECT_CAISSE}`
    // si aucune caisse n'est configurée, 409 si la table est déjà partie.
    handoffSessionToCaisse: build.mutation<unknown, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/handoff-to-caisse/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        return { data: result.data };
      },
    }),

    // Impression du total de la table sur les imprimantes de la caisse. La
    // caisse n'est qu'un média : rendu selon les règles cloud, aucune commande
    // ni document fiscal local. Chaque appel réimprime.
    printSessionTotal: build.mutation<PrintTotalQueued, { sessionUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/print-total/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: {},
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { destination?: string; dispatch_uuid?: string };
        return {
          data: {
            destination: (raw?.destination ?? '') as PrintTotalDestination,
            dispatchUuid: raw?.dispatch_uuid ?? '',
          },
        };
      },
    }),

    // SC-577 — ce que l'addition confiée à la sortie ticket est devenue. Sans
    // elle, le mobile annonçait « envoyée à l'impression » alors que personne
    // n'imprimait : le serveur repartait en salle et le papier ne sortait jamais.
    printTotalStatus: build.query<PrintTotalStatus, { dispatchUuid: string; token: string }>({
      queryFn: async ({ dispatchUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/print-total-status/${dispatchUuid}/`,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { state?: string; detail?: string; destination?: string };
        return {
          data: {
            state: (raw?.state ?? 'waiting') as PrintTotalState,
            detail: raw?.detail ?? '',
            destination: (raw?.destination ?? '') as PrintTotalDestination,
          },
        };
      },
    }),

    // SC-569 — les deux actions de table ci-dessus ne sortent du papier que si
    // le restaurant a une sortie ticket que le *serveur* atteint : imprimante
    // cloud en mode cloud, ou caisse liée. Le mobile n'a pas de C-Lodop et ne
    // voit pas cette configuration ; sans cette réponse il proposait « Imprimer
    // le total » partout, y compris là où il repartait en 400 SELECT_CAISSE.
    printCapabilities: build.query<PrintCapabilities, { token: string }>({
      queryFn: async ({ token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/print-capabilities/',
          headers: { Authorization: `Bearer ${token}` },
        });
        // Fail-closed, comme le serveur : une action masquée revient au
        // rafraîchissement suivant, une impression promise et jamais sortie
        // non. Mais l'échec ressort en `error`, plus en
        // `data: NO_PRINT_CAPABILITIES` : un « pas de sortie ticket » rangé
        // dans le cache comme une réponse RÉUSSIE ne se rafraîchit jamais —
        // ni retry, ni état d'erreur — et « Imprimer » restait absent jusqu'au
        // prochain rechargement complet, alors que le serveur répondait
        // `can_print_total: true`. Un seul échec passager suffisait (403
        // WAITER_REVOKED d'un autre onglet, requête annulée au montage).
        // `data` reste `undefined`, et tous les appelants lisent
        // `capabilities?.canPrintTotal` : l'affichage reste donc fail-closed.
        if (result.error) return { error: result.error };
        const raw = result.data as RawPrintCapabilities;
        return {
          data: {
            canPrintTotal: raw?.can_print_total === true,
            canSendToCaisse: raw?.can_send_to_caisse === true,
            canReprintKitchen: raw?.can_reprint_kitchen === true,
            caisseBridgeLinkBase:
              typeof raw?.caisse_bridge_link_base === 'string' ? raw.caisse_bridge_link_base : '',
          },
        };
      },
    }),

    // SC-1038 — les postes de cuisine que le serveur peut viser, par leur nom
    // de salle (« Cuisine chaude »). La liste vient du catalogue via sale_api :
    // le mobile ne connaît ni les SN cloud ni les noms C-Lodop, il ne rend
    // qu'un uuid à la réimpression.
    kitchenPrinters: build.query<KitchenPrinter[], { token: string }>({
      queryFn: async ({ token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/sale/order/waiter-mode/kitchen-printers/',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        const raw = (result.data ?? []) as { uuid?: string; name?: string }[];
        return { data: raw.map((p) => ({ uuid: p?.uuid ?? '', name: p?.name ?? '' })) };
      },
    }),

    // SC-1038 — ressortir le bon de cuisine de CE poste pour cette table. La
    // réponse a la forme de `printSessionTotal` (même routage, même ligne
    // d'attente, donc même `printTotalStatus` pour le suivi) ; 404
    // `PRINT_EMPTY` quand ce poste n'a aucun plat ici, ce qui n'est pas une
    // panne et se dit tel quel.
    kitchenReprintRounds: build.query<number[], { sessionUuid: string; printerUuid: string; token: string }>({
      queryFn: async ({ sessionUuid, printerUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/reprint-kitchen/`,
          params: { printer_uuid: printerUuid },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (result.error) return { error: result.error as never };
        return { data: (result.data as { round_numbers: number[] }).round_numbers };
      },
    }),

    // SC-1038 (recette QA 2026-09-22) — le même geste sur une commande web,
    // qui n'a pas de session : un seul poste, pas de tournée à choisir. Même
    // réponse que `reprintKitchen`, donc le même suivi.
    reprintOnlineOrderKitchen: build.mutation<PrintTotalQueued & { dispatchUuids: string[] }, { orderUuid: string; printerUuid: string; token: string }>({
      queryFn: async ({ orderUuid, printerUuid, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/online-orders/${orderUuid}/reprint-kitchen/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { printer_uuid: printerUuid },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { destination?: string; dispatch_uuid?: string; dispatch_uuids?: string[] };
        return {
          data: {
            destination: (raw?.destination ?? '') as PrintTotalDestination,
            dispatchUuid: raw?.dispatch_uuid ?? '',
            dispatchUuids: raw?.dispatch_uuids ?? (raw?.dispatch_uuid ? [raw.dispatch_uuid] : []),
          },
        };
      },
    }),

    reprintKitchen: build.mutation<PrintTotalQueued & { dispatchUuids: string[] }, { sessionUuid: string; printerUuid: string; roundNumber?: number; token: string }>({
      queryFn: async ({ sessionUuid, printerUuid, roundNumber, token }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/sale/order/waiter-mode/sessions/${sessionUuid}/reprint-kitchen/`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: { printer_uuid: printerUuid, ...(roundNumber === undefined ? {} : { round_number: roundNumber }) },
        });
        if (result.error) return { error: result.error as never };
        const raw = result.data as { destination?: string; dispatch_uuid?: string; dispatch_uuids?: string[] };
        return {
          data: {
            destination: (raw?.destination ?? '') as PrintTotalDestination,
            dispatchUuid: raw?.dispatch_uuid ?? '',
            dispatchUuids: raw?.dispatch_uuids ?? (raw?.dispatch_uuid ? [raw.dispatch_uuid] : []),
          },
        };
      },
    }),
  }),
});

export const {
  useWaiterPinPolicyQuery,
  useUnlockMutation,
  useSignOutMutation,
  useRenewTokenMutation,
  useWaiterTablesQuery,
  useOpenJoinTableMutation,
  useWaiterAreasQuery,
  useCreateAreaMutation,
  useCreateTableMutation,
  useUpdateTableMutation,
  useArchiveTableMutation,
  useOnlineOrdersQuery,
  useOnlineOrderDetailQuery,
  useSetOnlineOrderStatusMutation,
  useHandoffOnlineOrderToCaisseMutation,
  useFinishOnlineOrderMutation,
  useCancelOnlineOrderMutation,
  usePrintOnlineOrderReceiptMutation,
  useWaiterReservationsQuery,
  useWaiterReservationCountsQuery,
  usePrintWaiterReservationMutation,
  useSetWaiterReservationStatusMutation,
  useCreateWaiterReservationMutation,
  useUpdateWaiterReservationMutation,
  useLookupLoyaltyCodeMutation,
  useRedeemLoyaltyCouponMutation,
  useOfferLoyaltyRewardMutation,
  useListLoyaltyBillsQuery,
  useCreditLoyaltyBillMutation,
  useCancelLoyaltyCreditMutation,
  useListTakeawayQuery,
  useCreateTakeawayMutation,
  useRenameTakeawayMutation,
  useJoinTakeawayMutation,
  useGetSessionDetailQuery,
  usePostRoundMutation,
  useFireRoundMutation,
  useFinishSessionMutation,
  useCancelSessionMutation,
  useDecrementItemMutation,
  useValidateOrderMutation,
  useRefuseOrderMutation,
  useTakeOverRoundMutation,
  useCloseSessionMutation,
  useUnlockSessionOrderingMutation,
  useAttachTableMutation,
  useDetachTableMutation,
  useRegroupSessionMutation,
  useTransferSessionMutation,
  useUpdateSessionCoversMutation,
  useHandoffSessionToCaisseMutation,
  usePrintSessionTotalMutation,
  usePrintCapabilitiesQuery,
  useLazyPrintTotalStatusQuery,
  useKitchenPrintersQuery,
  useReprintKitchenMutation,
  useReprintOnlineOrderKitchenMutation,
  useKitchenReprintRoundsQuery,
  useLazyPushKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
} = waiterApi;
